import { useCallback, useEffect, useState } from 'react'
import { Typography, Box, Divider, Alert, Link } from '@mui/material'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { LoginForm } from 'src/components/LoginForm'
import { SocialAuth } from 'src/components/SocialAuth'
import { useAuth } from 'src/contexts/AuthContext'
import backgroundImage from 'src/images/background/sign_background.svg'
import { FIREBASE_REDIRECT_ERROR_KEY } from 'src/utils/authBrowser'

type LoginLocationState = { from?: { pathname: string } }

export function LoginPage() {
  const [error, setError] = useState<string>()
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const readRedirectError = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(FIREBASE_REDIRECT_ERROR_KEY)
      if (raw) {
        sessionStorage.removeItem(FIREBASE_REDIRECT_ERROR_KEY)
        const parsed = JSON.parse(raw) as { code?: string; message?: string }
        const hint =
          parsed.code === 'auth/unauthorized-domain'
            ? ' Vérifie que le domaine du site est dans Firebase → Authentication → Paramètres → Domaines autorisés.'
            : ''
        setError(`Connexion (redirect) : ${parsed.message ?? parsed.code ?? raw}.${hint}`)
      }
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    readRedirectError()
  }, [readRedirectError])

  /** L’erreur est souvent écrite après `getRedirectResult` (async) : relire quand l’auth a fini d’initialiser. */
  useEffect(() => {
    if (loading) return
    readRedirectError()
  }, [loading, readRedirectError])

  useEffect(() => {
    if (loading || !user) return
    const from = (location.state as LoginLocationState | null)?.from?.pathname
    navigate(from && from !== '/login' ? from : '/dashboard', { replace: true })
  }, [user, loading, navigate, location.state])

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left */}
      <Box
        sx={{
          flex: '1 1 50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 5 },
          py: 4,
          backgroundColor: 'white'
        }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#4277b9', mb: 6, textAlign: 'center' }}>
            BatiKal
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 4, textAlign: 'center' }}>
            Se connecter
          </Typography>

          <SocialAuth setError={setError} />

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>ou</Typography>
          </Divider>

          <LoginForm setError={setError} />

          <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: '#64748b' }}>
            Pas de compte ?{' '}
            <Link component={RouterLink} to="/signup" sx={{ color: '#4277b9', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Créer un compte
            </Link>
          </Typography>

          {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px', fontSize: '0.85rem' }}>{error}</Alert>}
        </Box>

        <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 'auto', pt: 4 }}>
          &copy; BatiKal {new Date().getFullYear()}
        </Typography>
      </Box>

      {/* Right */}
      <Box
        sx={{
          flex: '1 1 50%',
          display: { xs: 'none', md: 'block' },
          background: `url(${backgroundImage}) center / cover no-repeat`
        }}
      />
    </Box>
  )
}
